import React, { Suspense, useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { DataProvider } from './context/DataContext';
import { Layout } from './components/Layout';
import { ErrorBoundary } from './components/ErrorBoundary';

// Dynamisches Route-basiertes Code-Splitting
const Login = React.lazy(() => import('./pages/Login').then(m => ({ default: m.Login || (m as any).default })));
const Dashboard = React.lazy(() => import('./pages/Dashboard').then(m => ({ default: m.Dashboard || (m as any).default })));
const Zeiterfassung = React.lazy(() => import('./pages/Zeiterfassung').then(m => ({ default: m.Zeiterfassung || (m as any).default })));
const Angebote = React.lazy(() => import('./pages/Angebote').then(m => ({ default: m.Angebote || (m as any).default })));
const Karte = React.lazy(() => import('./pages/Karte').then(m => ({ default: m.Karte || (m as any).default })));
const Maschinen = React.lazy(() => import('./pages/Maschinen').then(m => ({ default: m.Maschinen || (m as any).default })));
const Lager = React.lazy(() => import('./pages/Lager').then(m => ({ default: m.Lager || (m as any).default })));
const CRM = React.lazy(() => import('./pages/CRM').then(m => ({ default: m.CRM || (m as any).default })));
const Rechnungen = React.lazy(() => import('./pages/Rechnungen').then(m => ({ default: m.Rechnungen || (m as any).default })));
const Admin = React.lazy(() => import('./pages/Admin').then(m => ({ default: m.Admin || (m as any).default })));
const Impressum = React.lazy(() => import('./pages/Impressum').then(m => ({ default: m.Impressum || (m as any).default })));
const Datenschutz = React.lazy(() => import('./pages/Datenschutz').then(m => ({ default: m.Datenschutz || (m as any).default })));
const Fuhrpark = React.lazy(() => import('./pages/Fuhrpark').then(m => ({ default: m.Fuhrpark || (m as any).default })));
const Auftraege = React.lazy(() => import('./pages/Auftraege').then(m => ({ default: m.Auftraege || (m as any).default })));
const Disposition = React.lazy(() => import('./pages/Disposition').then(m => ({ default: m.Disposition || (m as any).default })));
const Einsatzplan = React.lazy(() => import('./pages/Einsatzplan').then(m => ({ default: m.Einsatzplan || (m as any).default })));
const Mitarbeiter = React.lazy(() => import('./pages/Mitarbeiter').then(m => ({ default: m.Mitarbeiter || (m as any).default })));
const CustomerPortal = React.lazy(() => import('./pages/CustomerPortal').then(m => ({ default: m.CustomerPortal || (m as any).default })));
const CustomerTracking = React.lazy(() => import('./pages/CustomerTracking').then(m => ({ default: m.CustomerTracking || (m as any).default })));
const AuditLog = React.lazy(() => import('./pages/AuditLog').then(m => ({ default: m.AuditLog || (m as any).default })));
const Lieferanten = React.lazy(() => import('./pages/Lieferanten').then(m => ({ default: m.Lieferanten || (m as any).default })));
const LandingPage = React.lazy(() => import('./pages/LandingPage').then(m => ({ default: m.LandingPage || (m as any).default })));

// Leichtgewichtiger Fallback-Spinner für Page-Transitions
const PageFallback = () => (
  <div style={{
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '60vh',
    width: '100%',
    color: 'var(--ms-blue-primary, #0078d4)',
    fontFamily: 'sans-serif'
  }}>
    <div style={{
      width: '36px',
      height: '36px',
      border: '3px solid rgba(0, 120, 212, 0.2)',
      borderTopColor: '#0078d4',
      borderRadius: '50%',
      animation: 'spin 0.8s linear infinite'
    }} />
    <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
  </div>
);

const ProtectedRoute = ({ children, roles, moduleId }: { children: React.ReactNode, roles: string[], moduleId?: string }) => {
  const { currentUser, currentTenant, isPlatformAdmin } = useAuth();
  
  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }
  
  if (roles && !roles.includes(currentUser.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  if (moduleId) {
    // Platform admins can access anything
    if (isPlatformAdmin) {
      // access granted
    } else if (currentUser.role === 'Admin' && moduleId === 'verwaltung') {
      // Admin bypasses module check ONLY for 'verwaltung' to avoid self-lockout
      // access granted
    } else {
      const tenantModules = currentTenant?.activeModules || [];
      if (!tenantModules.includes(moduleId)) {
        return <Navigate to="/dashboard" replace />;
      }

      const userModules = currentUser.activeModules || [];
      if (!userModules.includes(moduleId)) {
        return <Navigate to="/dashboard" replace />;
      }
    }
  }
  
  return <Layout>{children}</Layout>;
};

const AppRoutes = () => {
  const { isLoading, currentUser } = useAuth();
  const [forceReady, setForceReady] = useState(false);
  const [showOverride, setShowOverride] = useState(false);

  useEffect(() => {
    // Show direct start button after 2 seconds if still loading
    const buttonTimer = setTimeout(() => setShowOverride(true), 2000);
    // Hard auto-dismiss after 3.5 seconds so user is never stuck
    const autoDismissTimer = setTimeout(() => setForceReady(true), 3500);
    return () => {
      clearTimeout(buttonTimer);
      clearTimeout(autoDismissTimer);
    };
  }, []);

  if (isLoading && !forceReady) {
    return (
      <div style={{ 
        height: '100vh', display: 'flex', flexDirection: 'column', 
        alignItems: 'center', justifyContent: 'center', gap: '24px',
        background: 'linear-gradient(145deg, #1a3654 0%, #2d5f8a 40%, #4A6FA5 100%)',
        color: 'white', fontFamily: 'sans-serif'
      }}>
        <div className="loader" style={{ 
          width: '48px', height: '48px', border: '5px solid #FFF', 
          borderBottomColor: 'transparent', borderRadius: '50%',
          display: 'inline-block', boxSizing: 'border-box',
          animation: 'rotation 1s linear infinite'
        }}></div>
        <style>{`
          @keyframes rotation { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
        `}</style>
        <div style={{ textAlign: 'center' }}>
          <h2 style={{ fontSize: '1.25rem', marginBottom: '8px' }}>Betriebszentrale</h2>
          <p style={{ opacity: 0.8, fontSize: '0.9rem' }}>Daten werden synchronisiert...</p>
          {showOverride && (
            <button
              onClick={() => setForceReady(true)}
              style={{
                marginTop: '16px',
                padding: '8px 18px',
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                color: '#ffffff',
                border: '1px solid rgba(255, 255, 255, 0.4)',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '0.85rem',
                fontWeight: 600
              }}
            >
              Direkt starten
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <Suspense fallback={<PageFallback />}>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/portal/:token" element={<CustomerPortal />} />
        <Route path="/portal/auftrag/:token" element={<CustomerTracking />} />
        <Route path="/" element={currentUser ? <Navigate to="/dashboard" replace /> : <LandingPage />} />
        <Route path="/dashboard" element={<ProtectedRoute roles={['Admin', 'Employee']}><Dashboard /></ProtectedRoute>} />
        <Route path="/zeiterfassung" element={<ProtectedRoute roles={['Admin', 'Employee']} moduleId="zeiterfassung"><ErrorBoundary><Zeiterfassung /></ErrorBoundary></ProtectedRoute>} />
        <Route path="/angebote" element={<ProtectedRoute roles={['Admin', 'Employee']} moduleId="angebote"><Angebote /></ProtectedRoute>} />
        <Route path="/karte" element={<ProtectedRoute roles={['Admin', 'Employee']} moduleId="karte"><Karte /></ProtectedRoute>} />
        <Route path="/maschinen" element={<ProtectedRoute roles={['Admin', 'Employee']} moduleId="maschinen"><Maschinen /></ProtectedRoute>} />
        <Route path="/lager" element={<ProtectedRoute roles={['Admin', 'Employee']} moduleId="lager"><Lager /></ProtectedRoute>} />
        <Route path="/crm" element={<ProtectedRoute roles={['Admin', 'Employee']} moduleId="crm"><CRM /></ProtectedRoute>} />
        <Route path="/lieferanten" element={<ProtectedRoute roles={['Admin', 'Employee']} moduleId="lieferanten"><Lieferanten /></ProtectedRoute>} />
        <Route path="/rechnungen" element={<ProtectedRoute roles={['Admin', 'Employee']} moduleId="rechnungen"><Rechnungen /></ProtectedRoute>} />
        <Route path="/admin" element={<ProtectedRoute roles={['Admin']} moduleId="verwaltung"><Admin /></ProtectedRoute>} />
        <Route path="/fuhrpark" element={<ProtectedRoute roles={['Admin', 'Employee']} moduleId="fuhrpark"><Fuhrpark /></ProtectedRoute>} />
        <Route path="/auftraege" element={<ProtectedRoute roles={['Admin', 'Employee']} moduleId="auftraege"><Auftraege /></ProtectedRoute>} />
        <Route path="/disposition" element={<ProtectedRoute roles={['Admin', 'Employee']} moduleId="auftraege"><Disposition /></ProtectedRoute>} />
        <Route path="/einsatzplan" element={<ProtectedRoute roles={['Admin', 'Employee']} moduleId="einsatzplan"><Einsatzplan /></ProtectedRoute>} />
        <Route path="/mitarbeiter" element={<ProtectedRoute roles={['Admin', 'Employee']} moduleId="mitarbeiter"><Mitarbeiter /></ProtectedRoute>} />
        <Route path="/protokoll" element={<ProtectedRoute roles={['Admin', 'Employee']} moduleId="protokoll"><AuditLog /></ProtectedRoute>} />
        <Route path="/impressum" element={<Layout><Impressum /></Layout>} />
        <Route path="/datenschutz" element={<Layout><Datenschutz /></Layout>} />
        <Route path="*" element={<div style={{padding: 40, color: 'black'}}><h2>Ungültige Seite. Pfad: {window.location.pathname}</h2><br/><a href="/">Zurück zur Startseite</a></div>} />
      </Routes>
    </Suspense>
  );
};

export const App = () => {
  return (
    <AuthProvider>
      <DataProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </DataProvider>
    </AuthProvider>
  );
};

export default App;
